// Phase 4c.7 — KPI tile. V-shape-native: resolves the `:value` (and
// optional `:delta`) `ColumnRef`s to columns, takes the latest value
// (the `rows.at(-1)` semantics the prior panel used), and delegates
// rendering to the existing `StandardKpi` leaf unchanged.

import type { ReactNode } from 'react';

import { StandardKpi } from '#core/components/dashboard/panels/kpi/variants/StandardKpi.tsx';
import type { NumFormat } from '#core/lib/expr/ops/dashboard/enums.ts';
import type { MetricSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import type { Cell } from '#core/lib/expr/value.ts';

import { useResolvedColumns } from './useResolvedColumns.ts';

const COMPACT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

/** Apply the tile's `:format` to the latest cell value. */
function formatMetric(value: Cell | undefined, format: NumFormat): ReactNode {
  if (typeof value !== 'number') {
    // Non-numeric (string label, null, nested Dataset) — render as-is
    // / blank; StandardKpi handles strings verbatim.
    if (value == null || typeof value === 'object') {
      return '—';
    }
    return String(value);
  }
  switch (format) {
    case 'compact':
      return COMPACT.format(value);
    case 'integer':
      return Math.round(value).toLocaleString('en');
    case 'percent':
      return `${value.toLocaleString('en', { maximumFractionDigits: 1 })}%`;
    case 'currency':
      return `$${value.toLocaleString('en', { maximumFractionDigits: 2 })}`;
    default:
      // `decimal` — hand the raw number to StandardKpi, which formats
      // with thousands separators via Mantine's NumberFormatter.
      return value;
  }
}

/**
 * Percent change of the delta column's last two points → a
 * StandardKpi trend indicator. Returns `{}` when there isn't enough
 * finite data to compute a change.
 */
function computeTrend(deltaCol: readonly Cell[]): { trend?: 'up' | 'down' | 'neutral'; trendValue?: number } {
  const latest = Number(deltaCol.at(-1));
  const prev = Number(deltaCol.at(-2));
  if (!Number.isFinite(latest) || !Number.isFinite(prev) || prev === 0) {
    return {};
  }
  const trendValue = ((latest - prev) / Math.abs(prev)) * 100;
  if (trendValue > 0) {
    return { trend: 'up', trendValue };
  }
  if (trendValue < 0) {
    return { trend: 'down', trendValue };
  }
  return { trend: 'neutral', trendValue };
}

export function MetricPanel({ spec }: { spec: MetricSpec }) {
  const refs = spec.delta === undefined ? [spec.value] : [spec.value, spec.delta];
  const cols = useResolvedColumns(refs);
  const value = formatMetric(cols[0]?.at(-1), spec.format);
  const { trend, trendValue } = spec.delta === undefined ? {} : computeTrend(cols[1] ?? []);

  return <StandardKpi label={spec.label} trend={trend} trendValue={trendValue} value={value} />;
}
